import { Alert, Tabs, Typography } from "../../../../../../../shared/frontend/ui";
import { DetailField, DetailList } from "../../../../../../../shared/frontend/components/DetailList";
import React from "react";
import type { ApiClient } from "../../../app/api/client";
import { spacing } from "../../../app/design-system";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { formatDateTimeIso } from "../../../app/units/format";
import type { UnitsSettings } from "../../../app/units/units";
import { PersonDocumentPreviewContent } from "../../../components/DocumentPreviewContent";
import { EmailBodyView } from "../../../components/EmailBodyView";
import { LoadingOverlay } from "../../../components/LoadingOverlay";
import { findPersonDocument } from "./personDigitalFileDocuments";
import type { PersonDigitalFileTimelineRow } from "./personDigitalFileTimelineUtils";
import type { PersonDocumentRow } from "./personDetailTypes";
import type { PersonEmailDetail } from "./personEmailDetail";
import "./personDigitalFileViewer.css";

export type PersonDigitalFileSurface = "document" | "email";

type Props = {
  api: ApiClient;
  personId: string;
  text: I18nContextValue["text"];
  units: UnitsSettings;
  row: PersonDigitalFileTimelineRow;
  document: PersonDocumentRow | null;
  surface: PersonDigitalFileSurface;
  onSurfaceChange: (surface: PersonDigitalFileSurface) => void;
  onDocumentResolved: (doc: PersonDocumentRow | null) => void;
};

/** Archivansicht: das Schreiben selbst; Mahnvorgang und Gebühren gehören ins Mahnwesen. */
export function PersonDigitalFileViewer({ api, personId, text, units, row, document, surface,
  onSurfaceChange, onDocumentResolved }: Props) {
  const [emailDetail, setEmailDetail] = React.useState<PersonEmailDetail | null>(null);
  const [resolvedDoc, setResolvedDoc] = React.useState<PersonDocumentRow | null>(document);
  const [loading, setLoading] = React.useState(row.source !== "document");
  const [loadErrorKey, setLoadErrorKey] = React.useState<string | null>(null);

  // Der Elternbereich montiert je Akteneintrag eine neue Ansicht. Späte Antworten dürfen
  // weder die Datei noch die Druck-/Downloadaktionen des nächsten Eintrags überschreiben.
  React.useEffect(() => {
    if (row.source === "document") return;
    let cancelled = false;
    const resolveDocument = async (docId: string | null | undefined) => {
      if (!docId) return null;
      const match = await findPersonDocument(api, personId, docId);
      return match?.hasFile === false ? null : match;
    };
    const load = async () => {
      let documentId = row.attachmentDocumentId;
      let emailQueueId = row.emailQueueId;
      if (row.source === "reminder" && row.reminderId) {
        const detail = await api.get<{ attachedDocumentId?: string | null; attachedEmailQueueId?: string | null }>(
          `/api/v1/reminders/${encodeURIComponent(row.reminderId)}`);
        if (cancelled) return;
        documentId = detail.attachedDocumentId || documentId;
        emailQueueId = detail.attachedEmailQueueId || emailQueueId;
      }

      // Datei und Nachricht unabhängig laden: Ein Mailfehler darf den archivierten Brief
      // nicht unsichtbar machen. Die Mahnungsdetails dienen nur zur Belegzuordnung.
      const [file, email] = await Promise.allSettled([
        resolveDocument(documentId),
        emailQueueId ? api.get<PersonEmailDetail>(`/api/v1/email-queues/${encodeURIComponent(emailQueueId)}`) : Promise.resolve(null)
      ]);
      if (cancelled) return;
      const detail = email.status === "fulfilled" ? email.value : null;
      setEmailDetail(detail);
      let match = file.status === "fulfilled" ? file.value : null;
      if (!match && detail?.attachmentDocumentId) match = await resolveDocument(detail.attachmentDocumentId);
      if (cancelled) return;
      setResolvedDoc(match);
      onDocumentResolved(match);
      if (match && !detail) onSurfaceChange("document");
      else if (!match && detail) onSurfaceChange("email");
      setLoadErrorKey(file.status === "rejected" ? "Document preview could not be loaded."
        : email.status === "rejected" ? "Error loading email" : null);
    };
    void load().catch(() => { if (!cancelled) setLoadErrorKey("Failed to load content"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [api, onDocumentResolved, onSurfaceChange, personId, row.attachmentDocumentId, row.emailQueueId, row.reminderId, row.source]);

  const documentPreview = resolvedDoc ? <PersonDocumentPreviewContent api={api} personId={personId}
    doc={resolvedDoc} text={text} zoomStorageKey="m1member.digital-file" /> : null;
  const showEmail = Boolean(emailDetail) && (surface === "email" || !resolvedDoc);
  const hasBoth = Boolean(emailDetail && resolvedDoc);

  return <div className="person-digital-file-viewer">
    <LoadingOverlay visible={loading} scope="block">
      <div className="person-digital-file-viewer-content">
        {hasBoth ? <Tabs activeKey={surface} onChange={key => onSurfaceChange(key as PersonDigitalFileSurface)} items={[
          { key: "email", label: text("Email") },
          { key: "document", label: resolvedDoc?.fileName || text("Attachment") }
        ]} /> : null}
        {loadErrorKey ? <Alert type="warning" showIcon title={text(loadErrorKey)} /> : null}
        <div style={{ flex: 1, minHeight: 0, overflow: showEmail ? "auto" : "hidden" }}>
          {showEmail && emailDetail ? <div style={{ display: "grid", gap: spacing.md, padding: spacing.md }}>
            <DetailList>
              <DetailField label={text("From")}>{emailDetail.fromEmail || "–"}</DetailField>
              <DetailField label={text("To")}>{emailDetail.toName ? `${emailDetail.toName} <${emailDetail.toEmail || ""}>` : emailDetail.toEmail || "–"}</DetailField>
              {emailDetail.sentAt ? <DetailField label={text("Sent")}>{formatDateTimeIso(emailDetail.sentAt, units)}</DetailField> : null}
            </DetailList>
            {emailDetail.errorMessage ? <Alert type="error" title={emailDetail.errorMessage} /> : null}
            {emailDetail.body?.trim() ? <EmailBodyView body={emailDetail.body} isHtml={emailDetail.isHtml} />
              : <Typography.Text type="secondary">{text("No content.")}</Typography.Text>}
            {!resolvedDoc && emailDetail.hasAttachment ? <Typography.Text type="secondary">
              {text("Attachment")}: {emailDetail.attachmentFileName || text("Document")}
            </Typography.Text> : null}
          </div> : documentPreview || (!loading ? <Typography.Text type="secondary">{text("No content.")}</Typography.Text> : null)}
        </div>
      </div>
    </LoadingOverlay>
  </div>;
}
